import { useCallback, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useUpdateHeaderData } from "@/hooks/useUpdateHeaderData";
import { Budge, Table, RowWithBudge, Button } from "@/components";
import { Actions } from "./Actions";
import { UserSidebarV2 } from "./sidebar/UserV2/UserV2";
import styles from "./stylev2.module.scss";
import { UserV2 } from "@/types/userv2";
import { Box } from "@mui/material";
import Title from "./TitleWithActionV2/titleWithActionV2";
import { useResponseErrorAlert } from "@/hooks/useResponseErrorAlert";
import { apiV2 } from "@/lib/apiV2/apiV2";
import { mapUserV2ToPayload } from "@/lib/helper/mappers/mapUserV2ToPayload";
import { action, APPLICATION_NAMES, regionColors, RegionKey } from "@/types";
import { useUserMeV2Info } from "@/hooks/useUserMeV2Info";
import { TAG_TYPE_NAME } from "@/types/tagsV2";
import { useMutation } from "@tanstack/react-query";
import { isAbleToActivateUser } from "@/lib/utils/userV2ContextUtils";

interface Props {
  data: UserV2[];
}

enum QUERY {
  MANAGE_USER = "manage_user",
}

enum STATUS {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

enum FILTERS {
  NAME = "name",
  EMAIL = "email",
  ROLE = "role_name",
  APPLICATION = "application_name",
  REGIONS = "region_name",
  STATUS = "status",
  CLIENTS = "clients",
  PROVIDER = "provider_name",
}

const prepareFilters = (query: URLSearchParams) => {
  const filters = {} as Record<string, string[]>;
  Object.values(FILTERS).forEach((key) => {
    if (query.has(key)) {
      filters[key] = query.get(key)!.split(",");
    }
  });
  return Object.keys(filters).length ? filters : undefined;
};
export interface SubmitMutationProps {
  data: UserV2;
  operation: action;
}

export const UsersTabV2 = ({ data = [] }: Props) => {
  const { canViewOnlyAgileProjects, isAbleToActOnUser, isAbleToActivateUser } =
    useUserMeV2Info();
  const [users, setUsers] = useState(data);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);

  const handleChangePage = useCallback(
    (newPage: number) => setCurrentPage(newPage),
    []
  );

  const handleChangeUsersPerPage = useCallback(
    (value) => setUsersPerPage(value),
    []
  );

  const manageUser = searchParams.get(QUERY.MANAGE_USER);

  const activeFilters = useMemo(
    () => prepareFilters(searchParams),
    [searchParams]
  );

  const manageUserData = useMemo(
    () => (manageUser ? users.find((user) => user.id === +manageUser) : null),
    [manageUser, users]
  );

  const usersData = useMemo(() => {
    const usersWithCustomFields = users.map((user) => {
      const editable = isAbleToActOnUser(user);
      const canActivate = !isAbleToActivateUser(user);

      return {
        ...user,
        application_name: user.applications.map(
          (application) => application.name
        ),
        role_name: user.role_associations.map((r) => r.name),
        region_name: user.role_associations.flatMap((role) =>
          role.tags
            .filter((tag) => tag.tag_type === TAG_TYPE_NAME.REGION)
            .map((tag) => tag.name)
        ),
        provider_name: user.role_associations.flatMap((role) =>
          role.tags
            .filter((tag) => tag.tag_type === TAG_TYPE_NAME.PROVIDER)
            .map((tag) => tag.name)
        ),
        status: user.active ? STATUS.ACTIVE : STATUS.INACTIVE,
        editable,
        canActivate,
      };
    });

    return canViewOnlyAgileProjects
      ? usersWithCustomFields.filter((user) =>
          user.application_name.includes(APPLICATION_NAMES.FAST_MK2)
        )
      : usersWithCustomFields;
  }, [users, canViewOnlyAgileProjects, isAbleToActOnUser]);

  useUpdateHeaderData(
    "users",
    <Actions action="user" query={QUERY.MANAGE_USER} />
  );

  const handleFiltering = useCallback(
    (key: keyof UserV2, value: Array<string | number>) => {
      setSearchParams((query) => {
        if (value.length) {
          query.set(key, value.join(","));
        } else {
          query.delete(String(key));
        }
        return query;
      });
      setCurrentPage(1);
    },
    [setSearchParams]
  );

  const handleUserEdit = useCallback(
    ({ id }: UserV2) => {
      setSearchParams((query) => {
        query.set(QUERY.MANAGE_USER, String(id));
        return query;
      });
    },
    [setSearchParams]
  );

  const handleClose = useCallback(() => {
    setSearchParams((query) => {
      query.delete(QUERY.MANAGE_USER);
      return query;
    });
  }, [setSearchParams]);

  const onError = useResponseErrorAlert();

  const toggleUserActiveStatus = useCallback(
    async (user: UserV2) => {
      try {
        await apiV2.users.patch(
          user.id,
          mapUserV2ToPayload({ ...user, active: !user.active })
        );
        return true;
      } catch (error) {
        onError(error);
        return false;
      }
    },
    [onError]
  );

  const handleActivate = useCallback(
    async (user: UserV2) => {
      const success = await toggleUserActiveStatus(user);

      if (!success) return;

      setUsers((state) => {
        const idx = state.findIndex((item) => item.id === user.id);
        const dataToEdit = [...state];
        dataToEdit[idx] = {
          ...dataToEdit[idx],
          active: !dataToEdit[idx].active,
        };
        return dataToEdit;
      });
    },
    [toggleUserActiveStatus]
  );

  const handleSubmit = useCallback(
    (data: UserV2, operation: "edit" | "create") => {
      setUsers((state) => {
        if (operation === "create") {
          return [data, ...state];
        }
        const idx = state.findIndex((item) => item.id === +data.id);
        const dataToEdit = [...state];
        dataToEdit[idx] = { ...dataToEdit[idx], ...data };
        return dataToEdit;
      });
      handleClose();
    },
    [handleClose]
  );

  const getUpdaterEmail = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => {
      map.set(user.email, user.name);
    });
    return map;
  }, [users]);

  const updaterRerenderer = (email: string) => {
    if (!email) return "--";
    const name = getUpdaterEmail.get(email);
    return name ? name : email;
  };

  const ColumnWithAction = useMemo(
    () =>
      ({ row, children }: { row: UserV2; children: React.ReactNode }) => {
        return (
          <div
            onClick={() => {
              if (!row.editable) return;
              handleUserEdit(row);
            }}
          >
            {children}
          </div>
        );
      },
    [handleUserEdit]
  );

  const getActionButtons = useCallback(
    (row: UserV2) => {
      return row.canActivate
        ? [
            {
              label: "Edit User",
              onSelect: () => handleUserEdit(row),
            },
            {
              label: row.active ? "Deactivate User" : "Activate User",
              onSelect: () => handleActivate(row),
            },
          ]
        : [
            {
              label: "Edit User",
              onSelect: () => handleUserEdit(row),
            },
          ];
    },
    [handleUserEdit, handleActivate]
  );

  return (
    <>
      <section className={styles.usersTable}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "25px",
          }}
        >
          <Title />
        </Box>
        <Table
          data={usersData}
          columns={columns}
          emptyMsg="There are no users to display yet."
          config={{
            columnWrapper: {
              name: ColumnWithAction,
              email: ColumnWithAction,
              role_name: ColumnWithAction,
              application_name: ColumnWithAction,
              clients: ColumnWithAction,
              region_name: ColumnWithAction,
              provider_name: ColumnWithAction,
              markets: ColumnWithAction,
              status: ColumnWithAction,
              updated_at: ColumnWithAction,
              updater: ColumnWithAction,
            },
            columnRenderer: {
              name: emptyModifier,
              application_name: namesJoiner,
              role_name: namesJoiner,
              clients: RowWithBudge,
              region_name: RegionsRenderer,
              provider_name: namesJoiner,
              markets: RowWithBudge,
              status: StatusRenderer,
              updated_at: dateModifiedRenderer,
              updater: updaterRerenderer,
            },
            activeFilters,
            onFilter: handleFiltering,
            sortBy: {
              name: true,
              email: true,
              role_name: true,
              application_name: true,
              region_name: true,
              provider_name: true,
              markets: true,
              status: true,
              updated_at: true,
              updater: true,
            },
            defaultSortByColumn: "name",
            filterBy: {
              name: false,
              email: false,
              role_name: true,
              application_name: true,
              region_name: true,
              provider_name: true,
              markets: true,
              status: true,
              clients: true,
            },
            searchBy: {
              name: true,
              email: true,
            },
            actions: getActionButtons,
            defaultOrder: "asc",
            styles: {
              name: { width: "15%" },
              email: { width: "15%" },
              role_name: { width: "10%" },
              region_name: { width: "15%" },
              provider_name: { width: "15%" },
              markets: { width: "15%" },
              active: { width: "fit-content" },
              application_name: { width: "10%" },
              updated_at: { maxWidth: "max-content" },
              updater: { maxWidth: "max-content" },
            },
            isPaginationEnabled: true,
            pagination: {
              currentPage: currentPage,
              pageSize: usersPerPage,
              onPageChange: (value) => handleChangePage(Number(value)),
              onPageSizeChange: (value) => handleChangeUsersPerPage(value),
            },
          }}
        />
      </section>
      {manageUser && (
        <UserSidebarV2
          onClose={handleClose}
          onSubmit={handleSubmit}
          userData={manageUserData}
        />
      )}
    </>
  );
};

const StatusRenderer = (value: string) => (
  <Budge
    color={value === STATUS.ACTIVE ? "black" : "white"}
    background={value === STATUS.ACTIVE ? "#17e88f" : "#778f9c"}
  >
    {value}
  </Budge>
);

const MAX_REGIONS_LENGTH = 4;

const RegionsRenderer = (regions: RegionKey[]) => {
  const uniqueRegions = new Set<string>(regions);

  const regionsArray = [...uniqueRegions];

  if (!regionsArray || regionsArray.length === 0) return <span>--</span>;

  return (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      {regionsArray.length < MAX_REGIONS_LENGTH ? (
        regionsArray.map((region) => {
          const { color, background } =
            regionColors[region] || regionColors.OTHER;
          return (
            <Budge key={region} color={color} background={background}>
              {region}
            </Budge>
          );
        })
      ) : (
        <Budge
          color={regionColors.ALL.color}
          background={regionColors.ALL.background}
        >
          ALL
        </Budge>
      )}
    </div>
  );
};

const emptyModifier = (value: string) => value || "--";

const namesJoiner = (values: string[]) => values.join(", ") || "--";

const dateModifiedRenderer = (value: string) =>
  value ? dayjs(value).format("MM/DD/YYYY") : "--";

const columns = {
  name: "User Name",
  email: "Email",
  application_name: "Application",
  role_name: "Role",
  provider_name: "Provider",
  region_name: "Region",
  markets: "Market",
  status: "Status",
  updated_at: "Last Updated",
  updater: "Updated By",
};
